import { useState } from 'react'

import type { Expense, Income } from '@subaio/api-types/budget'
import { ButtonGroup, ChevronFlip, Collapse, FormattedMessage } from '@subaio/components'

import { ServiceType } from 'enums'

import { Cell } from 'components/Lists'
import { useModal } from 'components/Modal'

import { ButtonLink } from './ButtonLink'
import { Container } from './Container'
import { Content } from './Content'
import { Header } from './Header'
import { UncategorizeForm } from './UncategorizedForm'
import { UncategorizedService } from './UncategorizedService'

const defaultVisibleServices = 3

type Props = {
	isEnabled: boolean
	currency: string
	categoriesMap: Array<{
		label: string
		value: {
			id: string
			subCategoryId: string
		}
	}>

	onRecategorize: (args: { categoryId: string; subCategoryId: string; serviceId: string }) => Promise<void>
} & (
	| {
			type: ServiceType.Expense
			uncategorized: Array<Expense>
	  }
	| {
			type: ServiceType.Income
			uncategorized: Array<Income> | null
	  }
)

export const Uncategorized = ({ type, isEnabled, currency, uncategorized, categoriesMap, onRecategorize }: Props) => {
	const { showCard } = useModal()
	const [isOpen, setIsOpen] = useState(type === ServiceType.Expense)
	const [isFullListShown, setIsFullListShown] = useState(type === ServiceType.Expense)

	if (!uncategorized || uncategorized.length === 0 || !isEnabled) return null

	return (
		<Container>
			<Header isOpen={isOpen} onClick={() => setIsOpen((isOpen) => !isOpen)}>
				<Cell textAlign='center'>
					<ChevronFlip isActive={isOpen} bold />
				</Cell>
				<Cell shouldExpand>
					<FormattedMessage
						id='flow.budgetEditForm.uncategorized.title'
						defaultMessage='{type, select,
							expense {Select the expenses to include in your budget}
							other {Should these income sources be included in your budget?}
						}'
						values={{ type }}
					/>
				</Cell>
			</Header>
			<Collapse isOpen={isOpen}>
				<Content>
					<p>
						<FormattedMessage
							id='flow.budgetEditForm.uncategorized.descriptionOne'
							defaultMessage="{type, select,
								expense {We have identified expenses that may be included in your budget and require your review.}
								other {Start by looking at the income below. We've found some income that we're not sure whether should be included in your budget.}
							}"
							values={{ type }}
						/>
					</p>
					<FormattedMessage
						id='flow.budgetEditForm.uncategorized.descriptionTwo'
						defaultMessage="{type, select,
							expense {Click on the relevant expenses and select the category they belong to.}
							other {Review the income and click ‘categorize’ to add them as recurring income in your budget. If some of the income is not relevant to the budget, you don't need to do anything about it.}
						}"
						values={{ type }}
					/>
				</Content>
				{uncategorized.slice(0, isFullListShown ? uncategorized.length : defaultVisibleServices).map((service) => (
					<UncategorizedService
						key={service.id}
						amountToShow='default'
						{...service}
						currency={currency}
						onClick={(service) =>
							showCard(({ onClose }) => (
								<UncategorizeForm
									type={type}
									service={service}
									currency={currency}
									categoriesMap={categoriesMap}
									onClose={onClose}
									onRecategorize={async (props) => {
										await onRecategorize(props)
										onClose()
									}}
								/>
							))
						}
					/>
				))}
				{uncategorized.length > defaultVisibleServices && (
					<ButtonGroup center>
						<ButtonLink
							label={
								isFullListShown ? (
									<FormattedMessage id='flow.budgetEditForm.uncategorized.labelShowLess' defaultMessage='Show less' />
								) : (
									<FormattedMessage id='flow.budgetEditForm.uncategorized.labelShowMore' defaultMessage='Show more' />
								)
							}
							onClick={() => setIsFullListShown((isFullListShown) => !isFullListShown)}
						/>
					</ButtonGroup>
				)}
			</Collapse>
		</Container>
	)
}
